import NavHeader from '../components/NavHeader'
import Footer from '../components/Footer'
import everyoneImage from '../assets/everyone.svg'

export default function AboutPage() {
  return (
    <div className='min-h-screen bg-white'>
      <NavHeader activeTab='about' />

      {/* Hero Section */}
      <div className='mx-auto px-4 sm:px-6 lg:px-20 py-12 md:py-16'>
        <div className='flex flex-col lg:flex-row items-start justify-between gap-8 lg:gap-12'>
          {/* Left Side - Title and Subtitle */}
          <div className='flex-1 text-start'>
            <h1 className='text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4'>
              About Us
            </h1>
            <p className='text-xl md:text-2xl text-gray-500 font-medium'>
              Simplifying UK Tax and Company Filings — for Everyone
            </p>
          </div>

          {/* Right Side - Introductory Paragraph */}
          <div className='flex-1'>
            <p className='text-base md:text-lg text-gray-600 leading-relaxed'>
              BritFiler was created with a simple mission: to make filing to HMRC
              and Companies House fast, accurate, and stress-free. We believe
              compliance shouldn't be complicated — so we built BritFiler to help
              individuals and businesses meet their filing obligations with ease
              and confidence.
            </p>
          </div>
        </div>
      </div>

      {/* Image and Our Story Section - Row Layout */}
      <div className='mx-auto px-4 sm:px-6 lg:px-20 mb-12 md:mb-16'>
        <div className='flex flex-col lg:flex-row items-start gap-8 lg:gap-12'>
          {/* Left Side - Image */}
          <div className='w-full lg:w-1/2 rounded-lg overflow-hidden flex-shrink-0'>
            <img
              src={everyoneImage}
              alt='Team collaboration'
              className='w-full h-auto object-cover rounded-lg'
            />
          </div>

          {/* Right Side - Our Story Content */}
          <div className='w-full lg:w-1/2 flex-1'>
            <h2 className='text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-start'>
              Our Story
            </h2>
            <div className='space-y-4 text-gray-600 leading-relaxed text-start'>
              <p>
                UK businesses and individuals face countless challenges when it
                comes to filing taxes and company returns. Confusing forms,
                complex regulations, and the constant fear of making mistakes
                that could lead to penalties — it's enough to make anyone
                frustrated.
              </p>
              <p>
                That frustration is exactly what led to BritFiler. We saw that
                there had to be a better way — a smart, reliable, user-friendly
                platform that could handle everything from Self Assessment to
                Corporation Tax, Annual Accounts to Confirmation Statements,
                without the headaches.
              </p>
              <p>
                Today, BritFiler is that platform. We're focused on one thing:
                making compliance simple, accurate, and stress-free for everyone.
                Simplicity, accuracy, and peace of mind — that's what drives us
                every single day.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className='mx-auto px-4 sm:px-6 lg:px-20 pb-12 md:pb-16 text-start'>
    

        {/* Our Mission & Our Vision - Two Column Layout */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16'>
          {/* Our Mission */}
          <div className='bg-gray-50 rounded-lg p-6 md:p-8'>
            <h2 className='text-2xl md:text-3xl font-bold text-gray-900 mb-4'>
              Our Mission
            </h2>
            <p className='text-gray-600 leading-relaxed'>
              To simplify compliance for everyone — by providing an intuitive,
              affordable, and fully compliant filing platform for individuals,
              businesses, and accountants across the UK.
            </p>
          </div>

          {/* Our Vision */}
          <div className='bg-gray-50 rounded-lg p-6 md:p-8'>
            <h2 className='text-2xl md:text-3xl font-bold text-gray-900 mb-4'>
              Our Vision
            </h2>
            <p className='text-gray-600 leading-relaxed'>
              To be the UK's most trusted and accessible platform for tax and
              company filings — empowering every business and individual to stay
              compliant effortlessly.
            </p>
          </div>
        </div>

        {/* Built for Trust & Compliance */}
        <div className='mb-12 md:mb-16'>
          <h2 className='text-3xl md:text-4xl font-bold text-gray-900 mb-6'>
            Built for Trust & Compliance
          </h2>
          <p className='text-gray-600 leading-relaxed mb-6'>
            BritFiler meets the highest UK standards for data security and
            compliance. We're fully integrated with:
          </p>
          <ul className='list-disc list-inside space-y-3 text-gray-600 mb-6 ml-4'>
            <li>
              <strong>HMRC (His Majesty's Revenue & Customs)</strong> — for Self
              Assessment and Corporation Tax (CT600) submissions.
            </li>
            <li>
              <strong>Companies House</strong> — for Annual Accounts and
              Confirmation Statement filings.
            </li>
          </ul>
          <p className='text-gray-600 leading-relaxed'>
            Every submission is protected with secure encryption, UK-based servers,
            and is designed to ensure full GDPR compliance — keeping your data safe
            at every step.
          </p>
        </div>

        {/* What Drives Us */}
        <div className='mb-12 md:mb-16'>
          <h2 className='text-3xl md:text-4xl font-bold text-gray-900 mb-6'>
            What Drives Us
          </h2>
          <ul className='space-y-4 text-gray-600'>
            <li className='flex items-start'>
              <span className='font-semibold text-gray-900 mr-2'>Simplicity:</span>
              <span>
                Filing should be easy for everyone — not just accountants.
              </span>
            </li>
            <li className='flex items-start'>
              <span className='font-semibold text-gray-900 mr-2'>Accuracy:</span>
              <span>
                Built-in validation and checks keep you error-free.
              </span>
            </li>
            <li className='flex items-start'>
              <span className='font-semibold text-gray-900 mr-2'>
                Transparency:
              </span>
              <span>No hidden costs, no surprises.</span>
            </li>
            <li className='flex items-start'>
              <span className='font-semibold text-gray-900 mr-2'>Security:</span>
              <span>Bank-grade protection for every submission.</span>
            </li>
            <li className='flex items-start'>
              <span className='font-semibold text-gray-900 mr-2'>Support:</span>
              <span>Real people ready to help when you need it.</span>
            </li>
          </ul>
        </div>

        {/* Our Commitment */}
        <div className='mb-12 md:mb-16'>
          <h2 className='text-3xl md:text-4xl font-bold text-gray-900 mb-6'>
            Our Commitment
          </h2>
          <p className='text-lg md:text-xl text-gray-600 leading-relaxed'>
            BritFiler isn't just software — it's a promise. A promise that you'll
            always have a reliable, secure, and simple way to meet your UK
            compliance needs. We're here to save you time, reduce your stress, and
            keep you compliant — every single time.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  )
}
